import React, { useCallback, useMemo } from 'react';
import { Box, Grid, Typography, Button } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Header from 'components/Header/Header.js';
import HeaderLinks from 'components/Header/HeaderLinks.js';
import HomeFooter from 'components/Footer/HomeFooter.js';
import QuickBooking from 'components/QuickBooking';
import useStyles from '../styles/styles';
import { FONT_FAMILY } from '../common/sharedFunctions';
import eatsLogo from '../assets/img/eatslogo.png';
import backgroundImage from '../assets/img/background.jpg';
import { EAT_GREEN } from '../common/eatBrand';

const WebReservation = (props) => {
  const { ...rest } = props;
  const classes = useStyles();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const settings = useSelector(state => state.settingsdata.settings);
  const auth = useSelector(state => state.auth);
  const loggedIn = !!auth?.profile?.uid;
  const rawLang = i18n?.language || i18n?.languages?.[0] || 'fr';
  const lang = rawLang.toLowerCase().startsWith('fr') ? 'fr' : 'en';

  const localTranslations = useMemo(() => ({
    fr: {
      quick_book: 'Réserver en ligne',
      pickup_place: 'Adresse de départ',
      drop_place: 'Destination',
      book_now: 'CONTINUER LA RÉSERVATION',
      contact_us: 'Besoin d’aide ?',
      quick_book_note: 'Votre trajet est transmis au même parcours de réservation que dans l’application Allô Driver.',
      title: 'Réservation VTC en ligne',
      subtitle: 'Préparez votre trajet, choisissez votre véhicule et finalisez la course avec votre compte client.',
      foodTitle: 'Allô Driver Eats reste dans l’app',
      foodText: 'Pour commander un plat, suivez un livreur en direct et retrouver vos reçus, utilisez l’application mobile.',
      backHome: 'Retour accueil'
    },
    en: {
      quick_book: 'Book online',
      pickup_place: 'Pickup address',
      drop_place: 'Destination',
      book_now: 'CONTINUE BOOKING',
      contact_us: 'Need help?',
      quick_book_note: 'Your route continues through the same booking flow used in the Allô Driver app.',
      title: 'Online VTC Booking',
      subtitle: 'Prepare your trip, choose a vehicle and confirm the ride with your customer account.',
      foodTitle: 'Allô Driver Eats stays in the app',
      foodText: 'Use the mobile app to order a dish, track a courier live and find your receipts.',
      backHome: 'Back home'
    }
  }), []);

  const localT = useCallback((key) => {
    if (localTranslations[lang]?.[key]) return localTranslations[lang][key];
    if (localTranslations.fr?.[key]) return localTranslations.fr[key];
    try {
      const value = t(key);
      if (value && value !== key) return value;
    } catch (e) {}
    return key;
  }, [lang, localTranslations, t]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#050505', color: '#fff' }}>
      <Header
        color="transparent"
        routes={[]}
        rightLinks={<HeaderLinks />}
        fixed
        changeColorOnScroll={{ height: 140, color: 'primary' }}
        {...rest}
      />

      <Box
        sx={{
          minHeight: { xs: 'auto', md: '100vh' },
          pt: { xs: 12, md: 16 },
          pb: { xs: 6, md: 10 },
          px: { xs: 2, md: 5 },
          backgroundImage: `linear-gradient(90deg, rgba(0,0,0,0.94) 0%, rgba(0,0,0,0.72) 52%, rgba(3,29,17,0.78) 100%), url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Grid container spacing={5} alignItems="center" sx={{ maxWidth: 1180, mx: 'auto' }}>
          <Grid item xs={12} md={6}>
            <Box component="img" src={eatsLogo} alt="Allô Driver Eats" sx={{ width: 92, height: 92, borderRadius: 2, mb: 3 }} />
            <Typography
              variant="h1"
              sx={{
                fontFamily: FONT_FAMILY,
                fontSize: { xs: 38, md: 64 },
                lineHeight: 1.05,
                fontWeight: 800,
                mb: 2,
              }}
            >
              {localT('title')}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.78)', fontSize: { xs: 16, md: 20 }, lineHeight: 1.65, maxWidth: 560 }}>
              {localT('subtitle')}
            </Typography>

            <Box sx={{ mt: 4, p: 3, borderRadius: 2, border: '1px solid rgba(6,193,103,0.36)', backgroundColor: 'rgba(6,193,103,0.08)', maxWidth: 560 }}>
              <Typography sx={{ color: EAT_GREEN, fontWeight: 800, mb: 1 }}>{localT('foodTitle')}</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.74)', lineHeight: 1.55 }}>{localT('foodText')}</Typography>
            </Box>

            <Button
              variant="outlined"
              onClick={() => navigate('/')}
              sx={{ mt: 4, color: '#fff', borderColor: 'rgba(255,255,255,0.32)', textTransform: 'none', borderRadius: 2 }}
            >
              {localT('backHome')}
            </Button>
          </Grid>

          <Grid item xs={12} md={5} sx={{ ml: { md: 'auto' } }}>
            <QuickBooking
              localT={localT}
              settings={settings}
              classes={classes}
              loggedIn={loggedIn}
              isMobile={false}
              accentColor={EAT_GREEN}
            />
          </Grid>
        </Grid>
      </Box>

      <HomeFooter />
    </Box>
  );
};

export default WebReservation;